var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { JobProvider, JobType, JobState } from './job';
import { DatasetProvider } from './dataset';
import { WhiteLabelProvider } from './white-label';
import { BoostProvider, STATE_DESCRIPTION_RECALCULATE } from './boost';
import { TranslateService } from '@ngx-translate/core';
/**
 * Return string descriptions for various elements of the sidebar job component
 */
var SidebarJobProvider = /** @class */ (function (_super) {
    __extends(SidebarJobProvider, _super);
    function SidebarJobProvider(jobProvider, datasetProvider, whiteLabelProvider, boostProvider, translate) {
        var _this = _super.call(this) || this;
        _this.jobProvider = jobProvider;
        _this.datasetProvider = datasetProvider;
        _this.whiteLabelProvider = whiteLabelProvider;
        _this.boostProvider = boostProvider;
        _this.translate = translate;
        return _this;
    }
    /**
     * Return a brief descriptor of what the job is currently doing
     */
    SidebarJobProvider.prototype.getAction = function (job) {
        switch (job.type) {
            case JobType.IncrementalTrain:
                return this.getTrainAction(job);
            case JobType.Retrain:
                return this.getRetrainAction(job);
            case JobType.Optimize:
                return this.getOptimizeAction(job);
            case JobType.Freeze:
                return this.getFreezeAction(job);
            case JobType.Train:
                return this.getTrainAction(job);
            case JobType.AnomalyHiFi:
                return this.getAnomalyHiFiAction(job);
            case JobType.BrainExport:
                return this.getBrainExportAction(job);
            default:
                return job.state;
        }
    };
    /**
     * Return the text for the tooltip component of the specified job in the sidebar
     */
    SidebarJobProvider.prototype.getTooltip = function (job, isIndeterminate) {
        var br = '<br />';
        var rawDatasetText = this.getTooltipDataset(job);
        var datasetText = rawDatasetText ? "" + rawDatasetText + br : '';
        var action = this.getTooltipAction(job);
        var status = this.getTooltipStatus(job, isIndeterminate);
        var advice = this.getTooltipAdvice(job);
        return "" + datasetText + action + br + status + br + advice;
    };
    SidebarJobProvider.prototype.getRetrainAction = function (job) {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');
            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Retraining');
            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');
            case JobState.CANCELED:
                return this.translate.instant('Canceled');
            case JobState.ERROR:
                return this.translate.instant('Error');
            case JobState.DONE:
                return this.translate.instant('Retrained');
            default:
                return job.state;
        }
    };
    SidebarJobProvider.prototype.getOptimizeAction = function (job) {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');
            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Optimizing');
            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');
            case JobState.CANCELED:
                return this.translate.instant('Canceled');
            case JobState.ERROR:
                return this.translate.instant('Error');
            case JobState.DONE:
                return this.translate.instant('Optimized');
            default:
                return job.state;
        }
    };
    SidebarJobProvider.prototype.getFreezeAction = function (job) {
        var dataset = this.getJobDataset(job);
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');
            case JobState.READY:
            case JobState.PROCESSING:
                // BB-5132 For now, display 'Rebuilding' only when a previously built frozen brain is present AND
                // when a subsequent build is still running
                return this.translate.instant('Building');
            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');
            case JobState.CANCELED:
                return this.translate.instant('Canceled');
            case JobState.ERROR:
                return this.translate.instant('Error');
            case JobState.DONE:
                return this.translate.instant('Built');
            default:
                return job.state;
        }
    };
    SidebarJobProvider.prototype.getTrainAction = function (job) {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');
            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Training');
            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');
            case JobState.CANCELED:
                return this.translate.instant('Canceled');
            case JobState.ERROR:
                return this.translate.instant('Error');
            case JobState.DONE:
                return this.translate.instant('Trained');
            default:
                return job.state;
        }
    };
    SidebarJobProvider.prototype.getAnomalyHiFiAction = function (job) {
        var dataset = this.getJobDataset(job);
        var isRerunning = dataset && dataset.hasAnomalyHiFiBrain();
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');
            case JobState.READY:
            case JobState.PROCESSING:
                return isRerunning ? this.translate.instant('Rebuilding') : this.translate.instant('Building');
            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');
            case JobState.CANCELED:
                return this.translate.instant('Canceled');
            case JobState.ERROR:
                return this.translate.instant('Error');
            case JobState.DONE:
                return this.translate.instant('Built');
            default:
                return job.state;
        }
    };
    SidebarJobProvider.prototype.getBrainExportAction = function (job) {
        switch (job.state) {
            case JobState.WAITING:
                return this.translate.instant('Waiting');
            case JobState.READY:
            case JobState.PROCESSING:
                return this.translate.instant('Exporting');
            case JobState.CANCEL_REQUESTED:
            case JobState.CANCELING:
                return this.translate.instant('Canceling');
            case JobState.CANCELED:
                return this.translate.instant('Canceled');
            case JobState.ERROR:
                return this.translate.instant('Error');
            case JobState.DONE:
                return this.translate.instant('Exported');
            default:
                return job.state;
        }
    };
    /**
     * Return the dataset associated with the specified job.
     * Return null if no dataset is associated with the job
     */
    SidebarJobProvider.prototype.getJobDataset = function (job) {
        if (job.datasetIdentifier) {
            return this.datasetProvider.getDatasetById(job.datasetIdentifier) || null;
        }
        return null;
    };
    SidebarJobProvider.prototype.getTooltipDataset = function (job) {
        if (job.datasetIdentifier) {
            var dataset = this.datasetProvider.getDatasetById(job.datasetIdentifier);
            if (dataset) {
                return dataset.name + " " + this.translate.instant('Dataset').toLowerCase();
            }
        }
        return null;
    };
    SidebarJobProvider.prototype.getTooltipAction = function (job) {
        return this.getSidebarJobTooltipJobType(job) + " " + this.getSidebarJobTooltipBrainType(job);
    };
    SidebarJobProvider.prototype.getSidebarJobTooltipJobType = function (job) {
        var dataset;
        var isRebuilding;
        switch (job.type) {
            case JobType.IncrementalTrain:
                return this.translate.instant('Training');
            case JobType.Retrain:
                return this.translate.instant('Retraining');
            case JobType.Optimize:
                return this.translate.instant('Optimizing');
            case JobType.Freeze:
                dataset = this.getJobDataset(job);
                return this.translate.instant('Building');
            case JobType.Train:
                return this.translate.instant('Training');
            case JobType.AnomalyHiFi:
                dataset = this.getJobDataset(job);
                // BB-5132 For now, display 'Rebuilding' only when a previously built hifi brain is present AND
                // when a subsequent build is still running
                isRebuilding = dataset && dataset.hasAnomalyHiFiBrain() && !this.jobProvider.isJobDone(job.state);
                return isRebuilding ? this.translate.instant('Rebuilding') : 'Building';
            case JobType.BrainExport:
                return this.translate.instant('Brain Export');
            default:
                return job.state;
        }
    };
    SidebarJobProvider.prototype.getSidebarJobTooltipBrainType = function (job) {
        switch (job.type) {
            case JobType.IncrementalTrain:
            case JobType.Retrain:
            case JobType.Optimize:
            case JobType.AnomalyHiFi:
                return this.datasetProvider.getRenderableDatasetTypeFromDatasetId(job.datasetIdentifier);
            case JobType.Freeze:
                return this.translate.instant('Static Classifier');
            case JobType.Train:
                return this.translate.instant('Detector');
            default:
                return '';
        }
    };
    SidebarJobProvider.prototype.getTooltipStatus = function (job, isIndeterminate) {
        switch (job.state) {
            case JobState.READY:
                return this.translate.instant('Ready');
            case JobState.WAITING:
                return this.translate.instant('Waiting');
            case JobState.PROCESSING:
                // We will follow the same pattern as on train/retrain/optimize screen:
                // in the components we subscribe to boost events, and in the case when job has stateDescription
                // we publish BoostRecalculating event (in the boost provider), in which case we show Finishing up... text
                if (job.stateDescription == STATE_DESCRIPTION_RECALCULATE) {
                    return this.translate.instant('Finishing up') + "...";
                }
                else if (!isIndeterminate) {
                    var translateReplacements_1 = {
                        progress: Math.round(this.jobProvider.getProgressPercentage(job))
                    };
                    return this.translate.instant('[[progress]]% complete', translateReplacements_1);
                }
                else {
                    return this.translate.instant('Processing') + "...";
                }
            case JobState.CANCELING:
            case JobState.CANCEL_REQUESTED:
                return this.translate.instant('Canceling');
            case JobState.CANCELED:
                return this.translate.instant('Canceled');
            case JobState.ERROR:
                return this.translate.instant('Error');
            case JobState.DONE:
                var translateReplacements = {
                    progress: 100
                };
                return this.translate.instant('[[progress]]% complete', translateReplacements);
            default:
                return '';
        }
    };
    SidebarJobProvider.prototype.getTooltipAdvice = function (job) {
        if (job.state == JobState.DONE) {
            return this.translate.instant('Click to evaluate your results');
        }
        else {
            return this.translate.instant('Click for more details');
        }
    };
    SidebarJobProvider = __decorate([
        Injectable(),
        __metadata("design:paramtypes", [JobProvider,
            DatasetProvider,
            WhiteLabelProvider,
            BoostProvider,
            TranslateService])
    ], SidebarJobProvider);
    return SidebarJobProvider;
}(BaseProvider));
export { SidebarJobProvider };
//# sourceMappingURL=sidebar-job.js.map


// WEBPACK FOOTER //
// ./src/providers/sidebar-job.ts.pre-build-optimizer.js